import {
  costsApi,
  overviewApi,
  projectDataApi,
  scheduleApi,
  tenderApi,
  timeApi,
  type PlanView,
  type Project,
  type ScheduleView,
} from '../api';
import { COST_FLAGS, COST_STAGES, COST_VALUE_KINDS, PACKAGE_STATUS, PROCEDURES, TASK_STATUS, TOTAL_LABEL, UNCATEGORIZED, formatDate } from '../format';
import { buildOutline } from '../modules/scheduling/outline';
import type { Cell, Sheet } from './sheet';

/**
 * The sheets the Mappe offers. Each one loads its data through the same
 * routes as the page it comes from, so it shows exactly what the page shows -
 * and only what the person may see there.
 */
export type SheetKind =
  | 'details' | 'contacts' | 'schedule' | 'costs-outline' | 'costs-lines' | 'costs-compare' | 'costs-values' | 'time' | 'tendering'
  | 'ov-time' | 'ov-costs' | 'ov-schedule';

export interface SheetChoice {
  kind: SheetKind;
  projectId?: string;
}

export const SHEET_KINDS: readonly { kind: SheetKind; label: string; permission: string; perProject: boolean }[] = [
  { kind: 'details', label: 'Stammdaten', permission: 'projectdata.read', perProject: true },
  { kind: 'contacts', label: 'Kontakte', permission: 'projectdata.read', perProject: true },
  { kind: 'schedule', label: 'Terminplan mit Balken', permission: 'scheduling.read', perProject: true },
  { kind: 'costs-outline', label: 'Kosten: Gliederung, alle Stufen', permission: 'costs.read', perProject: true },
  { kind: 'costs-lines', label: 'Kosten: Positionen der Bezugsstufe', permission: 'costs.read', perProject: true },
  { kind: 'costs-compare', label: 'Kosten: Variantenvergleich', permission: 'costs.read', perProject: true },
  { kind: 'costs-values', label: 'Kosten: Mengen, Kennwerte, Kennzahlen', permission: 'costs.read', perProject: true },
  { kind: 'time', label: 'Zeiten: Budget je Kategorie', permission: 'projectdata.read', perProject: true },
  { kind: 'tendering', label: 'Vergabeeinheiten', permission: 'tendering.read', perProject: true },
  { kind: 'ov-time', label: 'Alle Projekte: Zeiten', permission: 'projectdata.read', perProject: false },
  { kind: 'ov-costs', label: 'Alle Projekte: Kosten', permission: 'costs.read', perProject: false },
  { kind: 'ov-schedule', label: 'Alle Projekte: Termine und Dauern', permission: 'scheduling.read', perProject: false },
];

/** A built sheet; the schedule also carries what its bars are drawn from. */
export type BuiltSheet = Sheet & { schedule?: { plan: PlanView; schedule: ScheduleView } };

const stageLabel = (key: string): string => COST_STAGES.find((s) => s.key === key)?.label ?? key;
const hoursOf = (v: number | null): Cell => (v === null ? null : v);

export async function buildSheet(choice: SheetChoice, projects: readonly Project[]): Promise<BuiltSheet> {
  const project = projects.find((p) => p.id === choice.projectId);
  const pid = choice.projectId ?? '';
  const pname = project ? `${project.number} · ${project.name}` : '';
  const label = SHEET_KINDS.find((k) => k.kind === choice.kind)!.label;
  const named = (title: string): { name: string; title: string } => ({ name: project ? `${project.number} ${title}` : title, title: project ? `${pname} – ${title}` : title });

  switch (choice.kind) {
    case 'details': {
      const { details: d } = await projectDataApi.get(pid);
      const rows: Cell[][] = [
        ['Kurzname', d.shortName], ['Zeichen', d.reference], ['Auftraggebende', d.client], ['Beschreibung', d.description],
        ['Angebot vom', formatDate(d.offerDate)], ['Angebotssumme netto (€)', d.offerNet], ['Angebotssumme brutto (€)', d.offerGross],
        ['Bindefrist', formatDate(d.bindingUntil)], ['Fertig spätestens', formatDate(d.hardDeadline)], ['Planstand', d.planVersion],
      ];
      const bold: number[] = [];
      const section = (title: string, list: Cell[][]): void => { if (list.length) { bold.push(rows.length); rows.push([title, null]); rows.push(...list); } };
      section('Weitere Angaben', d.facts.map((f) => [f.label, f.value]));
      section('Planungsannahmen', d.assumptions.map((a) => [a.marker ? `[${a.marker}]` : '', a.text]));
      section('Risiken', d.risks.map((r) => [r.code ?? '', [r.title, r.measure].filter(Boolean).join(' → ')]));
      section('Eskalationsregeln', d.escalations.map((e) => ['', e]));
      return { ...named('Stammdaten'), columns: [{ label: 'Angabe', width: 28 }, { label: 'Wert', width: 90, format: 'amount' }], rows, boldRows: bold };
    }
    case 'contacts': {
      const { contacts } = await projectDataApi.get(pid);
      return {
        ...named('Kontakte'),
        subtitle: 'Personenbezogene Daten – nur für die Projektabwicklung weitergeben.',
        columns: ['Kürzel', 'Rolle', 'Organisation', 'Name', 'Funktion', 'E-Mail', 'Telefon'].map((l) => ({ label: l })),
        rows: contacts.map((c) => [c.code, c.role, c.organisation, c.name, c.function, c.email, c.phone]),
      };
    }
    case 'schedule': {
      const [plan, schedule] = await Promise.all([scheduleApi.plan(pid), scheduleApi.schedule(pid)]);
      const rows: Cell[][] = [];
      const bold: number[] = [];
      for (const r of buildOutline(schedule.groups, schedule.tasks, new Set())) {
        if (r.kind === 'group') {
          bold.push(rows.length);
          rows.push([r.group.code, r.group.name, formatDate(r.group.earlyStart), formatDate(r.group.earlyFinish), r.group.durationDays, null, null, null, null]);
        } else {
          const t = r.task;
          rows.push([t.code, t.name, formatDate(t.earlyStart), t.durationDays === 0 ? null : formatDate(t.earlyFinish), t.durationDays, t.owner, t.position, TASK_STATUS[t.status as keyof typeof TASK_STATUS]?.label ?? t.status, t.deliverable]);
        }
      }
      return {
        ...named('Terminplan'),
        subtitle: `${plan.name} · ${formatDate(schedule.projectStart)} – ${formatDate(schedule.earliestFinish)} · Ziel ${formatDate(schedule.targetFinish)}`,
        columns: [{ label: 'Nr.' }, { label: 'Vorgang', width: 60 }, { label: 'Beginn' }, { label: 'Ende' }, { label: 'AT', format: 'int' }, { label: 'Verantwortlich' }, { label: 'Position' }, { label: 'Status' }, { label: 'Ergebnis', width: 40 }],
        rows,
        boldRows: bold,
        schedule: { plan, schedule },
      };
    }
    case 'costs-outline': {
      const plan = await costsApi.get(pid);
      const result = plan.results.find((r) => r.variantId === plan.primaryVariantId) ?? plan.results[0]!;
      const variant = plan.variants.find((v) => v.id === result.variantId);
      const names = new Map(plan.groups.map((g) => [g.code, g.name]));
      const rows: Cell[][] = result.rollup.groups.map((g) => [g.code, names.get(g.code) ?? '', ...COST_STAGES.map((s) => g.stages[s.key]), g.committed || null]);
      const bold = result.rollup.groups.map((g, i) => (g.depth === 0 ? i : -1)).filter((i) => i >= 0);
      bold.push(rows.length);
      rows.push(['', 'Summe netto', ...COST_STAGES.map((s) => result.totals[s.key].net), result.rollup.committedTotal || null]);
      if (COST_STAGES.some((s) => result.totals[s.key].risk)) {
        rows.push(['', 'Risikozuschlag', ...COST_STAGES.map((s) => result.totals[s.key].risk), null]);
        bold.push(rows.length);
        rows.push(['', 'Summe mit Risiko', ...COST_STAGES.map((s) => result.totals[s.key].withRisk), null]);
      }
      return {
        ...named('Kosten'),
        subtitle: `Beträge netto in €${variant ? ` · Variante ${variant.code} ${variant.name}` : ''} · Bezugsstufe ${stageLabel(plan.referenceStage)}`,
        columns: [{ label: 'KG' }, { label: 'Bezeichnung', width: 40 }, ...COST_STAGES.map((s) => ({ label: s.label, format: 'amount' as const })), { label: 'Beauftragt', format: 'amount' }],
        rows,
        boldRows: bold,
      };
    }
    case 'costs-lines': {
      const plan = await costsApi.get(pid);
      const result = plan.results.find((r) => r.variantId === plan.primaryVariantId) ?? plan.results[0]!;
      const variant = plan.variants.find((v) => v.id === result.variantId);
      const stage = plan.referenceStage;
      const totals = new Map(result.rollup.groups.map((g) => [g.code, g.stages[stage]]));
      const rows: Cell[][] = [];
      const bold: number[] = [];
      for (const g of plan.groups) {
        const lines = plan.items.filter((i) => i.groupId === g.id && i.stage === stage && (i.variantId === null || i.variantId === result.variantId));
        if (lines.length === 0 && totals.get(g.code) === null) continue;
        bold.push(rows.length);
        rows.push([g.code, g.name, null, null, null, null, totals.get(g.code) ?? null, null]);
        for (const l of lines) {
          const r = result.lines[l.id];
          rows.push([l.flag ? COST_FLAGS[l.flag].short : '', l.description, r?.quantity ?? null, l.unit, r?.unitPrice ?? null, l.quantityFormula, r?.error ? `Fehler: ${r.error}` : (r?.amount ?? null), l.source]);
        }
      }
      bold.push(rows.length);
      rows.push(['', 'Summe netto', null, null, null, null, result.totals[stage].net, null]);
      return {
        ...named(`Positionen ${COST_STAGES.find((s) => s.key === stage)?.short ?? ''}`),
        subtitle: `${stageLabel(stage)}${variant ? ` · Variante ${variant.code} ${variant.name}` : ''} · Beträge netto in €`,
        columns: [{ label: 'Kz. / KG' }, { label: 'Position', width: 55 }, { label: 'Menge', format: 'quantity' }, { label: 'Einheit' }, { label: 'Kennwert (€)', format: 'amount' }, { label: 'Mengenansatz', width: 24 }, { label: 'Betrag (€)', format: 'amount' }, { label: 'Quelle', width: 30 }],
        rows,
        boldRows: bold,
      };
    }
    case 'costs-compare': {
      const plan = await costsApi.get(pid);
      const stage = plan.referenceStage;
      const heads = plan.results.map((r) => plan.variants.find((v) => v.id === r.variantId));
      const maps = plan.results.map((r) => new Map(r.rollup.groups.map((g) => [g.code, g])));
      const top = plan.groups.filter((g) => maps[0]?.get(g.code)?.depth === 0);
      const rows: Cell[][] = top.map((g) => [g.code, g.name, ...maps.map((m) => m.get(g.code)?.stages[stage] ?? null)]);
      const bold = [rows.length];
      rows.push(['', 'Summe netto', ...plan.results.map((r) => r.totals[stage].net)]);
      rows.push(['', 'Risikozuschlag', ...plan.results.map((r) => r.totals[stage].risk)]);
      bold.push(rows.length);
      rows.push(['', 'Summe mit Risiko', ...plan.results.map((r) => r.totals[stage].withRisk)]);
      rows.push(['', `Brutto (${plan.vatRate} % USt.)`, ...plan.results.map((r) => (r.totals[stage].withRisk === null ? null : Math.round(r.totals[stage].withRisk! * (1 + plan.vatRate / 100) * 100) / 100))]);
      rows.push(['', 'Bandbreite von', ...plan.results.map((r) => r.totals[stage].low)]);
      rows.push(['', 'Bandbreite bis', ...plan.results.map((r) => r.totals[stage].high)]);
      for (const v of plan.values.filter((x) => x.kind === 'ratio')) rows.push([v.name, `${v.label ?? v.name}${v.unit ? ` (${v.unit})` : ''}`, ...plan.results.map((r) => r.ratios[stage][v.name]?.value ?? null)]);
      return {
        ...named('Variantenvergleich'),
        subtitle: `${stageLabel(stage)} · netto in €`,
        columns: [{ label: 'KG' }, { label: 'Bezeichnung', width: 40 }, ...heads.map((h) => ({ label: h ? `${h.code} ${h.name}` : 'Summe', format: 'amount' as const }))],
        rows,
        boldRows: bold,
      };
    }
    case 'costs-values': {
      const plan = await costsApi.get(pid);
      const results = plan.variants.length ? plan.variants.map((v) => plan.results.find((r) => r.variantId === v.id)!) : [plan.results[0]!];
      const stage = plan.referenceStage;
      return {
        ...named('Werte'),
        subtitle: 'Mengen, Kennwerte und Kennzahlen je Variante',
        columns: [{ label: 'Name' }, { label: 'Bezeichnung', width: 50 }, { label: 'Art' }, { label: 'Ansatz', width: 28 }, ...(plan.variants.length ? plan.variants.map((v) => ({ label: v.code, format: 'quantity' as const })) : [{ label: 'Wert', format: 'quantity' as const }]), { label: 'Einheit' }],
        rows: plan.values.map((v) => [v.name, v.label, COST_VALUE_KINDS[v.kind].one, v.formula, ...results.map((r) => (v.kind === 'ratio' ? r.ratios[stage]?.[v.name]?.value : r.values[v.name]?.value) ?? null), v.unit]),
      };
    }
    case 'time': {
      const sheet = await timeApi.get(pid);
      const rows: Cell[][] = sheet.categories.map((c) => [c.code, c.label, hoursOf(c.budgetHours), c.booked, c.remaining, c.share === null ? null : Math.round(c.share * 1000) / 10, c.unpaid ? 'unbezahlt' : 'ja']);
      if (sheet.uncategorized.booked > 0) rows.push([UNCATEGORIZED, null, null, sheet.uncategorized.booked, null, null, null]);
      rows.push(['', TOTAL_LABEL, sheet.categories.reduce((s, c) => s + (c.budgetHours ?? 0), 0) || null, sheet.total, null, null, null]);
      return {
        ...named('Zeiten'),
        columns: [{ label: 'Kategorie' }, { label: 'Bezeichnung', width: 50 }, { label: 'Budget (h)', format: 'quantity' }, { label: 'erfasst (h)', format: 'quantity' }, { label: 'Rest (h)', format: 'quantity' }, { label: 'Verbrauch (%)', format: 'quantity' }, { label: 'bezahlt' }],
        rows,
        boldRows: [rows.length - 1],
      };
    }
    case 'tendering': {
      const list = await tenderApi.list(pid);
      return {
        ...named('Vergabe'),
        columns: [{ label: 'Nr.' }, { label: 'Vergabeeinheit', width: 40 }, { label: 'KG' }, { label: 'Verfahren', width: 26 }, { label: 'Status' }, { label: 'Frist' }, { label: 'Positionen', format: 'int' }, { label: 'Bietende', format: 'int' }, { label: 'Schätzung (€)', format: 'amount' }, { label: 'günstigstes (€)', format: 'amount' }, { label: 'Zuschlag an' }, { label: 'Auftrag (€)', format: 'amount' }],
        rows: list.map((p) => [p.code, p.name, p.costGroup, PROCEDURES.find((x) => x.value === p.procedure)?.label ?? p.procedure, PACKAGE_STATUS[p.status as keyof typeof PACKAGE_STATUS]?.label ?? p.status, formatDate(p.deadline), p.positions, p.bidders, p.estimate, p.lowest?.amount ?? null, p.awarded?.company ?? null, p.awarded?.amount ?? null]),
      };
    }
    case 'ov-time': {
      const ov = await overviewApi.time();
      const rows: Cell[][] = [];
      const bold: number[] = [];
      const listed = ov.projects.filter((x) => x.categories.length || x.uncategorized.booked);
      for (const p of listed) {
        bold.push(rows.length);
        rows.push([p.number, p.name, p.budget || null, p.total, p.mine]);
        for (const c of p.categories) rows.push(['', `${c.code}${c.label ? ` – ${c.label}` : ''}`, c.budgetHours, c.booked, c.mine || null]);
        if (p.uncategorized.booked) rows.push(['', UNCATEGORIZED, null, p.uncategorized.booked, p.uncategorized.mine || null]);
      }
      const sum = (f: (x: (typeof listed)[number]) => number): number => Math.round(listed.reduce((t, x) => t + f(x), 0) * 100) / 100;
      bold.push(rows.length);
      rows.push([TOTAL_LABEL, null, sum((x) => x.budget) || null, sum((x) => x.total), sum((x) => x.mine)]);
      return { name: 'Zeiten alle', title: label, columns: [{ label: 'Projekt' }, { label: 'Kategorie', width: 55 }, { label: 'Budget (h)', format: 'quantity' }, { label: 'erfasst (h)', format: 'quantity' }, { label: 'davon ich (h)', format: 'quantity' }], rows, boldRows: bold };
    }
    case 'ov-costs': {
      const ov = await overviewApi.costs();
      return {
        name: 'Kosten alle', title: label, subtitle: 'Hauptvariante, Summe mit Risiko, netto in €',
        columns: [{ label: 'Projekt' }, { label: 'Bezeichnung', width: 40 }, { label: 'Risiko (%)', format: 'quantity' }, ...COST_STAGES.map((s) => ({ label: s.label, format: 'amount' as const })), { label: 'Beauftragt', format: 'amount' }, { label: 'Bezugsstufe' }],
        rows: ov.map((r) => [r.number, r.name, r.riskPercent, ...COST_STAGES.map((s) => r.stages[s.key].withRisk), r.committed || null, stageLabel(r.referenceStage)]),
      };
    }
    case 'ov-schedule': {
      const ov = await overviewApi.schedule();
      return {
        name: 'Termine alle', title: label, subtitle: 'Tage = Kalendertage',
        columns: [{ label: 'Projekt' }, { label: 'Bezeichnung', width: 40 }, { label: 'Beginn' }, { label: 'Ende (Plan)' }, { label: 'Ziel' }, { label: 'Puffer (T)', format: 'int' }, { label: 'Ende (Basis)' }, { label: 'Abweichung (T)', format: 'int' }, { label: 'Dauer (T)', format: 'int' }, { label: 'erledigt', format: 'int' }, { label: 'Einträge', format: 'int' }, { label: 'nächster Meilenstein', width: 40 }],
        rows: ov.map((r) => [r.number, r.name, formatDate(r.start), formatDate(r.finish), formatDate(r.targetFinish), r.floatToTarget, r.baseline ? formatDate(r.baseline.finish) : null, r.finishDelta, r.durationDays, r.tasks.done, r.tasks.total, r.nextMilestone ? `${formatDate(r.nextMilestone.date)} ${r.nextMilestone.code} ${r.nextMilestone.name}` : null]),
      };
    }
  }
}
